import React from 'react';
import { Document, Page, Text, View } from '@react-pdf/renderer';
import PDFStyles, { ELECTRICAL_COLORS } from './styles/ElectricalInstallationCertificatePDF';
import { transformForPDF } from './transform';

const CircuitTable = ({ circuits }) => {
  const columns = [
    { label: 'No.', width: '5%' },
    { label: 'DB', width: '7%' },
    { label: 'Description', width: '20%' },
    { label: 'Location', width: '15%' },
    { label: 'Phase', width: '10%' },
    { label: 'Device', width: '12%' },
    { label: 'Rating', width: '7%' },
    { label: 'Max Zs', width: '8%' },
    { label: 'Measured Zs', width: '8%' },
    { label: 'Result', width: '8%' }
  ];

  return (
    <View style={PDFStyles.table}>
      {/* Header */}
      <View style={[PDFStyles.tableRow, PDFStyles.tableHeader]}>
        {columns.map(col => (
          <View key={col.label} style={{ width: col.width, ...PDFStyles.tableCell }}>
            <Text style={PDFStyles.tableCell}>{col.label}</Text>
          </View>
        ))}
      </View>
      {/* Rows */}
      {circuits.map((c, i) => (
        <View key={i} style={PDFStyles.tableRow}>
          <View style={{ width: '5%', ...PDFStyles.tableCell }}>
            <Text style={PDFStyles.tableCell}>{c.circuitNumber || i + 1}</Text>
          </View>
          <View style={{ width: '7%' }}>
            <Text style={PDFStyles.tableCell}>{c.dbName}</Text>
          </View>
          <View style={{ width: '20%' }}>
            <Text style={PDFStyles.tableCell}>{c.circuitDescription}</Text>
          </View>
          <View style={{ width: '15%' }}>
            <Text style={PDFStyles.tableCell}>{c.location}</Text>
          </View>
          <View style={{ width: '10%' }}>
            <Text style={PDFStyles.tableCell}>{c.phase}</Text>
          </View>
          <View style={{ width: '12%' }}>
            <Text style={PDFStyles.tableCell}>{c.protectiveDevice}</Text>
          </View>
          <View style={{ width: '7%' }}>
            <Text style={PDFStyles.tableCell}>{c.deviceRating}A</Text>
          </View>
          <View style={{ width: '8%' }}>
            <Text style={PDFStyles.tableCell}>{c.maxZs || ''}</Text>
          </View>
          <View style={{ width: '8%' }}>
            <Text style={PDFStyles.tableCell}>{c.measuredZs}</Text>
          </View>
          <View style={{ width: '8%' }}>
            <Text style={PDFStyles.tableCell}>{c.testResult}</Text>
          </View>
        </View>
      ))}
    </View>
  );
};

const ElectricalInstallationCertificatePDF = ({ formData }) => {
  const data = transformForPDF(formData);
  const assessmentColor = data.overallAssessment === 'SATISFACTORY' ? ELECTRICAL_COLORS.green : ELECTRICAL_COLORS.red;

  return (
    <Document>
      <Page size="A4" style={PDFStyles.page}>
        {/* Page footer */}
        <View style={PDFStyles.footer}>
          <Text>
            Certificate No: {data.declaration.certificateNumber}   |   Issued under {data.standard}   |   Page 
            {/* dynamic page count requires renderer hooks; use fixed text */}
          </Text>
        </View>

        {/* Header */}
        <View style={PDFStyles.headerPanel}>
          <Text style={PDFStyles.title}>{data.title}</Text>
          <Text style={PDFStyles.subtitle}>{data.standard}</Text>
          <Text style={PDFStyles.subtitle}>
            Certificate No: {data.declaration.certificateNumber} | Issued: {data.declaration.issueDate}
          </Text>
        </View>

        {/* Contractor */}
        <View style={PDFStyles.section}>
          <Text style={PDFStyles.sectionTitle}>1. Details of the Contractor (Installer)</Text>
          <View style={PDFStyles.fieldRow}>
            <Text style={PDFStyles.fieldLabel}>Company:</Text>
            <Text style={PDFStyles.fieldValue}>{data.contractor.companyName}</Text>
          </View>
          <View style={PDFStyles.fieldRow}>
            <Text style={PDFStyles.fieldLabel}>Address:</Text>
            <Text style={PDFStyles.fieldValue}>{data.contractor.address}</Text>
          </View>
          <View style={PDFStyles.fieldRow}>
            <Text style={PDFStyles.fieldLabel}>Email / Tel:</Text>
            <Text style={PDFStyles.fieldValue}>{data.contractor.email} / {data.contractor.phone}</Text>
          </View>
          <View style={PDFStyles.fieldRow}>
            <Text style={PDFStyles.fieldLabel}>Competent Scheme:</Text>
            <Text style={PDFStyles.fieldValue}>
              {data.contractor.schemeName} ({data.contractor.schemeReference})
            </Text>
          </View>
          <View style={PDFStyles.fieldRow}>
            <Text style={PDFStyles.fieldLabel}>Engineer:</Text>
            <Text style={PDFStyles.fieldValue}>
              {data.contractor.engineerName} - Card: {data.contractor.engineerCardNumber}
            </Text>
          </View>
          <View style={PDFStyles.fieldRow}>
            <Text style={PDFStyles.fieldLabel}>Notification Date:</Text>
            <Text style={PDFStyles.fieldValue}>{data.contractor.notificationDate}</Text>
          </View>
        </View>

        {/* Client */}
        <View style={PDFStyles.section}>
          <Text style={PDFStyles.sectionTitle}>2. Details of the Client</Text>
          <View style={PDFStyles.fieldRow}>
            <Text style={PDFStyles.fieldLabel}>Name:</Text>
            <Text style={PDFStyles.fieldValue}>{data.client.name}</Text>
          </View>
          <View style={PDFStyles.fieldRow}>
            <Text style={PDFStyles.fieldLabel}>Address:</Text>
            <Text style={PDFStyles.fieldValue}>{data.client.address}</Text>
          </View>
          <View style={PDFStyles.fieldRow}>
            <Text style={PDFStyles.fieldLabel}>Email / Tel:</Text>
            <Text style={PDFStyles.fieldValue}>{data.client.email} / {data.client.phone}</Text>
          </View>
        </View>

        {/* Installation */}
        <View style={PDFStyles.section}>
          <Text style={PDFStyles.sectionTitle}>3. Details of the Installation</Text>
          <View style={PDFStyles.fieldRow}>
            <Text style={PDFStyles.fieldLabel}>Address:</Text>
            <Text style={PDFStyles.fieldValue}>{data.installation.address}</Text>
          </View>
          <View style={PDFStyles.fieldRow}>
            <Text style={PDFStyles.fieldLabel}>Description:</Text>
            <Text style={PDFStyles.fieldValue}>{data.installation.description}</Text>
          </View>
          <View style={PDFStyles.fieldRow}>
            <Text style={PDFStyles.fieldLabel}>Extent / Limitation:</Text>
            <Text style={PDFStyles.fieldValue}>{data.installation.extentLimitation}</Text>
          </View>
          <View style={PDFStyles.fieldRow}>
            <Text style={PDFStyles.fieldLabel}>Installation Type:</Text>
            <Text style={PDFStyles.fieldValue}>{data.installation.type}</Text>
          </View>
          <View style={PDFStyles.fieldRow}>
            <Text style={PDFStyles.fieldLabel}>Main Supply:</Text>
            <Text style={PDFStyles.fieldValue}>
              {data.installation.mainSupply} | {data.installation.mainProtectiveDevice} | {data.installation.mainIsolator}
            </Text>
          </View>
        </View>

        {/* Supply Characteristics */}
        <View style={PDFStyles.section}>
          <Text style={PDFStyles.sectionTitle}>4. Supply Characteristics & Particulars at Origin</Text>
          <View style={PDFStyles.fieldRow}>
            <Text style={PDFStyles.fieldLabel}>Nominal Voltage:</Text>
            <Text style={PDFStyles.fieldValue}>{data.supplyCharacteristics.nominalVoltage} V</Text>
          </View>
          <View style={PDFStyles.fieldRow}>
            <Text style={PDFStyles.fieldLabel}>External Ze:</Text>
            <Text style={PDFStyles.fieldValue}>{data.supplyCharacteristics.externalZe || ''} Ω</Text>
          </View>
          <View style={PDFStyles.fieldRow}>
            <Text style={PDFStyles.fieldLabel}>Earthing Type:</Text>
            <Text style={PDFStyles.fieldValue}>{data.supplyCharacteristics.earthingType}</Text>
          </View>
          <View style={PDFStyles.fieldRow}>
            <Text style={PDFStyles.fieldLabel}>PSC:</Text>
            <Text style={PDFStyles.fieldValue}>{data.supplyCharacteristics.psc}</Text>
          </View>
          <View style={PDFStyles.fieldRow}>
            <Text style={PDFStyles.fieldLabel}>Main Bonding:</Text>
            <Text style={PDFStyles.fieldValue}>{data.supplyCharacteristics.mainBonding}</Text>
          </View>
        </View>

        {/* Schedule of Test Results */}
        <View style={PDFStyles.section} wrap={false}>
          <Text style={PDFStyles.sectionTitle}>5. Schedule of Test Results</Text>
          <CircuitTable circuits={data.circuits} />
        </View>

        {/* Overall Assessment */}
        <View style={[PDFStyles.assessment, { borderColor: assessmentColor }]} wrap={false}>
          <Text style={[PDFStyles.assessmentTitle, { color: assessmentColor }]}>
            Overall Assessment: {data.overallAssessment}
          </Text>
        </View>

        {/* Declaration */}
        <View style={PDFStyles.section}>
          <Text style={PDFStyles.sectionTitle}>6. Declaration</Text>
          <Text style={PDFStyles.declaration}>
            I hereby certify that the design and construction of the electrical installation
            to which this certificate relates, together with the tests specified in the
            schedule of test results, comply with the requirements of BS 7671:2018+A2:2022
            and the relevant Building Regulations.
          </Text>
        </View>

        {/* Signatures */}
        <View style={PDFStyles.signatureRow}>
          <View style={PDFStyles.signatureBox}>
            <Text>Engineer: {data.declaration.engineerSignature}</Text>
          </View>
          <View style={PDFStyles.signatureBox}>
            <Text>Client: {data.declaration.customerSignature}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default ElectricalInstallationCertificatePDF;